import React from 'react';
import { gql, useApolloClient } from '@apollo/client';
import Footer from './containers/Footer';
import { TranslateProvider } from './TranslationsProvider';
import Documentation from './containers/Documentation';
import Validation from './components/validation/ValidationProvider';
import Scrollbar from './components/complexComponents/Scrollbar';
import FormFactory from './containers/FormFactory';
import Home from './containers/Home';
import PortalHeader from './containers/PortalHeader';
import PopUpWrapper from './containers/PopUpWrapper';
import ConfirmationPopUp from './components/popUps/ConfirmationPopUp';
import LoadingPage from './components/complexComponents/LoadingPage';
import NotFound from './components/complexComponents/NotFound';
import ClosedPortal from './components/complexComponents/ClosedPortal';
import { usePolling } from '../utils/hooks';
import { MainAppContext } from './MainAppContext';
import { FORM_SECTIONS } from './utils/constants';

const Main = ({ defaultPortal = null, match }) => {
	/* MAIN APP CONTEXT; PORTAL DATA; COLORS AND ROUTING */
	const {
		portal,
		setPortal,
		colors,
		routing,
		updateRouting,
		showRoutingPopUp,
		setShowRoutingPopUp,
		confirmRoute,
		cancelRoute,
	} = React.useContext(MainAppContext);

	/* Window state */
	const [state, setState] = React.useState({
		isLoading: true,
		lookedOnce: false
	});

	/* GraphQL client */
	const client = useApolloClient();
	/* Translate object */
	const translate = React.useContext(TranslateProvider);
	/* Scrollbar reference */
	const scrollbar = React.useRef();
	/* Date */
	const dateString = new Date().toISOString();

	/* For editor display */
	React.useEffect(() => {
		if (defaultPortal !== null) {
			setPortal(defaultPortal);
		}
	}, [defaultPortal]);

	/**
	 * GET DATA FROM API
	 */
	const getData = React.useCallback(async () => {
		/* For editor display */
		if (defaultPortal !== null) {
			setPortal(defaultPortal);
			return;
		}
		setState({
			...state,
			isLoading: true,
		});
		const response = await client.query({
			query: gql`
                query CouncilPortal($name: String!){
                    councilPortal(name: $name){
                        name
                        id
                        state
                        config
                        councilId
                        council {
                            name
                            id
							street
							city
							language
							companyId
							company {
								id
								logo
								businessName
							}
                            dateStart
                            councilType
                            agendas {
								orderIndex
                                agendaSubject
                                subjectType
                                id
                                items {
                                    id
                                    value
                                }
                                options {
                                    maxSelections
                                    minSelections
                                    id
                                }
                            }
                        }
                        attachments {
                            id
                            filename
							showInHome
                        }
                    }
                }
            `,
			variables: {
				name: match.params.name
			}
		});
		if (response.data.councilPortal) {
			const defaultVotes = response?.data?.councilPortal?.config?.defaultVoteSense?.custom;
			const agendaVotes = response?.data?.councilPortal?.council?.agendas;
			const newVotes = [];
			Object.entries(defaultVotes).forEach(([key, value]) => {
				const elemAgenda = agendaVotes?.find(i => i.id === value.id);
				newVotes?.push({ ...defaultVotes[key], orderIndex: elemAgenda.orderIndex });
			});
			setPortal({
				...response.data.councilPortal,
				config: {
					...response.data.councilPortal?.config,
					defaultVoteSense: {
						...response.data.councilPortal?.config?.defaultVoteSense,
						custom: {
							...response.data.councilPortal?.config?.defaultVoteSense?.custom,
							...newVotes
						}
					}
				}
			});
		}
		setState({
			isLoading: false,
			lookedOnce: true
		});
	}, []);

	// Update default language
	React.useEffect(() => {
		translate.setDefaultLanguage(portal?.config?.languages?.default);
	}, [portal?.config?.languages?.default]);

	// Update when portal id changes
	React.useEffect(() => {
		getData();
	}, [match.params.name]);
	// Update each 10 seconds for hot changes
	usePolling(getData, defaultPortal ? 10000000 : 10000);

	// Scroll to top when changing between sections
	React.useEffect(() => scrollbar.current?.scrollToTop(), [routing]);

	/* PORTAL NOT FOUND */
	if ((!state.isLoading && !portal) || (!portal && state.lookedOnce)) {
		return <NotFound translate={translate} colors={colors} />;
	}

	/* LOADING SCREEN */
	if (state.isLoading && !portal) {
		return <LoadingPage translate={translate} colors={colors} />;
	}

	/* Check if the portal is open or closed */
	// if (
	// 	/* Activated or deactivated */
	// 	portal.state === 0
	// 	/* Open date */
	// 	|| (
	// 		dateString < portal.config.portalDates.open.general?.date
	// 	)
	// 	/* Close date */
	// 	|| (
	// 		dateString > portal.config.portalDates.close.general?.date
	// 	)
	// ) {
	// 	return <ClosedPortal
	// 		colors={colors}
	// 		portal={portal}
	// 		translate={translate}
	// 	/>;
	// }

	/* Adhesive Footer */
	const footerHeight = document?.getElementById('Footer')?.clientHeight;
	const defaultStyleWrapper = {
		minHeight: `calc(100vh - ${footerHeight}px)`,
	};

	/* MAIN RENDER */
	return <React.Fragment>
		<div
			id='MAIN'
			className='w-full h-screen md:overflow-y-auto'
			style={{ backgroundColor: colors?.background }}
		>
			<Scrollbar ref={scrollbar}>
				<div
					id='no_footer_wrapper'
					className='relative flex flex-col top-0 bottom-0 left-0 right-0'
					style={defaultStyleWrapper}
				>
					{/* HEADER */}
					{portal?.config?.header?.isActive
						&& <PortalHeader
							portal={portal}
							routing={routing}
							setRouting={updateRouting}
							colors={colors}
							translate={translate}
						/>}
					{/* MAIN SECTIONS */}
					<main className='min-h-full'>
						{/* HOME */}
						{routing === 'home'
							&& <Home
								portal={portal}
								colors={colors}
								translate={translate}
								routing={routing}
								setRouting={updateRouting}
							/>
						}

						{/* FORMS */}
						{FORM_SECTIONS.includes(routing)
							&& <Validation
								portal={portal}
								translate={translate}
							>
								<FormFactory
									portal={portal}
									colors={colors}
									formType={routing}
									setPortal={setPortal}
									onSuccess={() => updateRouting('home')}
									dateString={dateString}
									updateRouting={updateRouting}
									translate={translate} />
							</Validation>
						}

						{/* DOCUMENTATION */}
						{routing === 'documents'
							&& <section>
								<Documentation
									portal={portal}
									colors={colors}
									translate={translate}
								/>
							</section>
						}

						{/* NO SECTION */}
						{!routing
							&& <NotFound translate={translate} colors={colors} />}
					</main>
				</div>

				{/* FOOTER */}
				{portal?.config?.footer?.isActive
					&& <Footer
						data={portal?.config?.footer}
						translate={translate}
						colors={colors}
					/>}
			</Scrollbar>
		</div>

		{/* CONFIRM POPUP */}
		<PopUpWrapper
			colors={colors}
			open={showRoutingPopUp}
			setOpen={setShowRoutingPopUp}
		>
			<ConfirmationPopUp
				translate={translate}
				data={portal?.config?.popUps?.changeWindow}
				onConfirm={confirmRoute}
				onCancel={cancelRoute}
				colors={colors}
			/>
		</PopUpWrapper>
	</React.Fragment>;
};

export default Main;
